import { useState, useEffect, useRef, useCallback } from 'react';

const SWIPE_THRESHOLD = 10;
const DRAG_MAX = 120;
const COMMIT_RATIO = 0.35;
const DEAD_ZONE = 8;

/**
 * Shared bidirectional swipe + vertical-reorder hook for block cards.
 *
 * Supports left-drag (dx < 0) and right-drag (dx > 0) via touch AND mouse
 * events (desktop), AND vertical drag-to-reorder when enableReorder is true.
 *
 * **Horizontal swipe** (existing)
 *   - Left-drag  → move mode (select target node in sidebar)
 *   - Right-drag → options panel (delete + future options)
 *   Visual: edge-strip indicators grow from left/right edge.
 *
 * **Vertical reorder** (new — opt-in via enableReorder)
 *   - Drag card up/down past ~0.5 card height → reorder
 *   Visual: card follows pointer via translateY; .dragging class applied.
 *
 * Direction lock: once the drag crosses DEAD_ZONE, the dominant axis
 * (horizontal or vertical) is locked and the other axis is ignored
 * for the remainder of the gesture.
 *
 * @param {Object} opts
 * @param {boolean}  opts.enabled           - when false, vertical reorder is disabled; horizontal swipe always works (default true)
 * @param {boolean}  opts.moveMode          - disables all gestures when true
 * @param {Object}   opts.contentRefs       - React ref: index → content DOM element
 * @param {Object}   opts.cardRefs          - React ref: index → card DOM element (for reorder translateY)
 * @param {boolean}  opts.enableReorder     - enable vertical drag-to-reorder (default false)
 * @param {number}   opts.blockCount        - total block count (for reorder index clamping)
 * @param {Object}   opts.scrollContainerRef - React ref to the scrollable parent container (for auto-scroll during reorder)
 * @param {Function} opts.onSwipeProgress   - (direction: 'left'|'right', ratio: number) => void
 * @param {Function} opts.onSwipeCommit     - (direction: 'left'|'right', index: number) => void
 * @param {Function} opts.onSwipeCancel     - () => void
 * @param {Function} opts.onReorderEnd      - (fromIndex: number, toIndex: number) => void
 * @returns {{
 *   touchHandlers: { onTouchStart, onTouchMove, onTouchEnd },
 *   mouseHandlers: { onMouseDown },
 *   swipedIndex: number|null,
 *   swipeProgress: { index: number, direction: 'left'|'right', ratio: number }|null,
 *   swipeJustCommitted: React.Ref,
 *   closeSwipe: () => void,
 *   reorderState: { index: number, offsetY: number, dragIndex: number }|null,
 * }}
 */
export default function useBlockSwipe({
  enabled = true,
  moveMode,
  contentRefs,
  cardRefs,
  enableReorder = false,
  blockCount = 0,
  scrollContainerRef,
  onSwipeProgress,
  onSwipeCommit,
  onSwipeCancel,
  onReorderEnd,
}) {
  const [swipedIndex, setSwipedIndex] = useState(null);
  const [swipeProgress, setSwipeProgress] = useState(null);
  // { index, direction, ratio } | null — for visual edge-strip rendering

  const [reorderState, setReorderState] = useState(null);
  // { index, offsetY, dragIndex } | null — for optional drop-zone indicator

  const touchRef = useRef({
    startX: 0, startY: 0, index: null, deltaX: 0,
    locked: false, direction: null,
    // ── Reorder fields ─────────────────────────────────────────
    reorderActive: false, fromIndex: null, currentDragIndex: null,
    cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
  });
  const mouseActive = useRef(false);
  const swipeJustCommitted = useRef(false);

  // Refs to avoid stale closures in native event listeners
  const moveModeRef = useRef(moveMode);
  moveModeRef.current = moveMode;
  const enableReorderRef = useRef(enableReorder);
  enableReorderRef.current = enableReorder;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ── Close swipe ──────────────────────────────────────────────
  const closeSwipe = useCallback(() => {
    setSwipedIndex(null);
    setSwipeProgress(null);
  }, []);

  // Close swipe when moveMode activates
  useEffect(() => {
    if (moveMode && swipedIndex !== null) {
      setSwipedIndex(null);
      setSwipeProgress(null);
    }
  }, [moveMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Internal: vertical reorder drag-move ─────────────────────
  const handleReorderDrag = useCallback((clientY, index) => {
    const t = touchRef.current;
    const offsetY = clientY - t.startY;
    t.offsetY = offsetY;

    // Move the floating card via top (position: fixed was set at lock time)
    const cardEl = cardRefs?.current?.[index];
    if (cardEl) {
      cardEl.style.top = `${t.initialTop + offsetY}px`;
    }

    // ── Auto-scroll the container when pointer nears top/bottom edge ──
    // Skip on desktop (mouse) — auto-scroll fights manual reorder positioning.
    const scrollEl = scrollContainerRef?.current;
    if (scrollEl && !mouseActive.current) {
      const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight;
      if (hasOverflow) {
        const containerRect = scrollEl.getBoundingClientRect();
        const EDGE_ZONE = 80;       // px from edge to start auto-scroll
        const MAX_SPEED = 10;       // max px per move event
        const pointerRelY = clientY - containerRect.top;

        if (pointerRelY < EDGE_ZONE) {
          // Near top edge — scroll up proportionally
          const ratio = 1 - pointerRelY / EDGE_ZONE;
          scrollEl.scrollBy(0, -Math.round(MAX_SPEED * ratio));
        } else if (pointerRelY > containerRect.height - EDGE_ZONE) {
          // Near bottom edge — scroll down proportionally
          const distFromBottom = containerRect.height - pointerRelY;
          const ratio = 1 - distFromBottom / EDGE_ZONE;
          scrollEl.scrollBy(0, Math.round(MAX_SPEED * ratio));
        }
      }
    }

    // ── Compute dragIndex from actual card bounding rects ──────────
    // This naturally accounts for the gap left by the dragged card
    // (pulled out of flow via position:fixed) because getBoundingClientRect
    // returns the post-shift positions of the remaining cards.
    const cardH = t.cardHeight || 60;
    const draggedMidY = t.initialTop + offsetY + cardH / 2;

    let dragIndex = t.fromIndex;
    for (let i = 0; i < blockCount; i++) {
      if (i === t.fromIndex) continue;
      const otherCard = cardRefs?.current?.[i];
      if (!otherCard) continue;
      const rect = otherCard.getBoundingClientRect();
      // Skip cards with zero-area rects (not in DOM / display:none)
      if (rect.height === 0) continue;
      const otherMidY = rect.top + rect.height / 2;

      if (i < t.fromIndex && draggedMidY < otherMidY) {
        // Dragged card has risen above this card's midpoint → insert before it
        dragIndex = Math.min(dragIndex, i);
      } else if (i > t.fromIndex && draggedMidY > otherMidY) {
        // Dragged card has fallen below this card's midpoint → insert after it
        dragIndex = Math.max(dragIndex, i);
      }
    }

    if (dragIndex !== t.currentDragIndex) {
      t.currentDragIndex = dragIndex;
    }

    // Publish reorder state for drop indicator
    setReorderState(prev => {
      if (prev && prev.index === index && prev.dragIndex === dragIndex) return prev;
      return { index, offsetY, dragIndex };
    });
  }, [cardRefs, blockCount, scrollContainerRef]);

  // ── Shared drag-move logic ───────────────────────────────────
  const handleDragMove = useCallback((clientX, clientY, index) => {
    if (moveMode) return;
    const t = touchRef.current;
    if (t.index !== index) return;

    // If reorder is already active, keep processing vertical
    if (t.reorderActive) {
      handleReorderDrag(clientY, index);
      return;
    }

    const dx = clientX - t.startX;
    const dy = Math.abs(clientY - t.startY);

    // Lock direction: wait until movement crosses dead zone
    if (!t.locked && Math.abs(dx) < DEAD_ZONE && dy < DEAD_ZONE) return;
    if (!t.locked) {
      if (dy > Math.abs(dx)) {
        // ── VERTICAL lock — enter reorder mode (only if enabled) ──
        // Mouse drags always allow reorder; touch requires swipeEnabled (enabled).
        if (enableReorder && (enabled || mouseActive.current)) {
          t.locked = true;
          t.reorderActive = true;
          t.fromIndex = index;
          t.currentDragIndex = index;
          const cardEl = cardRefs?.current?.[index];
          if (cardEl) {
            // Capture natural measurements BEFORE pulling the card out of flow
            const rect = cardEl.getBoundingClientRect();
            t.cardHeight = cardEl.offsetHeight;
            t.cardWidth = rect.width;
            t.initialTop = rect.top;
            // Pull the card out of the flow → other cards collapse naturally
            cardEl.style.position = 'fixed';
            cardEl.style.left = `${rect.left}px`;
            cardEl.style.top = `${rect.top}px`;
            cardEl.style.width = `${rect.width}px`;
            cardEl.style.zIndex = '10';
            cardEl.style.touchAction = 'none';
            cardEl.classList.add('dragging');
          }
          // Suppress native scroll on the container during reorder so
          // programmatic auto-scroll doesn't fight the browser.
          const scrollEl = scrollContainerRef?.current;
          if (scrollEl) {
            t._origOverflowY = scrollEl.style.overflowY;
            scrollEl.style.overflowY = 'hidden';
          }
          return;
        }
        // Reorder disabled — don't lock; allow gesture to continue
        // so horizontal swipe can still engage
      }
      // ── HORIZONTAL lock (existing behavior) ──────────────────
      t.locked = true;
      t.direction = dx > 0 ? 'right' : 'left';
    }

    // After horizontal lock: discard vertical movement
    if (dy > Math.abs(dx)) return;

    // Clamp delta to DRAG_MAX + overshoot
    const maxDrag = DRAG_MAX + 20;
    t.deltaX = Math.min(maxDrag, Math.max(-maxDrag, dx));

    // Keep content still — no translateX
    const el = contentRefs.current[index];
    if (el) {
      el.style.transition = 'none';
      el.style.transform = '';
    }

    // Report progress to parent — cap visual ratio so the edge strip
    // locks at the commit threshold instead of growing indefinitely.
    const rawRatio = Math.min(1, Math.abs(t.deltaX) / DRAG_MAX);
    const visualRatio = Math.min(rawRatio, COMMIT_RATIO);
    if (onSwipeProgress) {
      onSwipeProgress(t.direction, rawRatio);
    }

    // Update visual edge-strip state for the swiped card
    setSwipeProgress({ index, direction: t.direction, ratio: visualRatio });
  }, [moveMode, contentRefs, onSwipeProgress, enableReorder, enabled, cardRefs, blockCount, handleReorderDrag]);

  // ── Shared drag-end logic ────────────────────────────────────
  const handleDragEnd = useCallback((index) => {
    if (moveMode) return;
    const t = touchRef.current;
    if (t.index !== index) return;

    // Restore touch-action on the card element regardless of how
    // the gesture ended — it may have been set to 'none' at touchstart
    // to prevent the browser from claiming the vertical pan.
    if (enableReorder && enabled) {
      const cardEl = cardRefs?.current?.[index];
      if (cardEl) {
        cardEl.style.touchAction = '';          // restore CSS pan-y
      }
    }

    // ── Reorder end ─────────────────────────────────────────
    if (t.reorderActive) {
      // Clean up card DOM styles — restore to normal flow
      const cardEl = cardRefs?.current?.[index];
      if (cardEl) {
        cardEl.style.position = '';
        cardEl.style.left = '';
        cardEl.style.top = '';
        cardEl.style.width = '';
        cardEl.style.zIndex = '';
        cardEl.style.touchAction = '';          // restore CSS pan-y
        cardEl.classList.remove('dragging');
      }

      // Restore scroll container overflow
      const scrollEl = scrollContainerRef?.current;
      if (scrollEl && t._origOverflowY !== undefined) {
        scrollEl.style.overflowY = t._origOverflowY;
      }

      // Suppress subsequent click if any drag movement occurred
      if (Math.abs(t.offsetY) > 5) {
        swipeJustCommitted.current = true;
      }

      const finalIndex = t.currentDragIndex ?? t.fromIndex;
      if (finalIndex !== t.fromIndex && onReorderEnd) {
        onReorderEnd(t.fromIndex, finalIndex);
      }

      // Reset reorder state
      setReorderState(null);
      touchRef.current = {
        startX: 0, startY: 0, index: null, deltaX: 0,
        locked: false, direction: null,
        reorderActive: false, fromIndex: null, currentDragIndex: null,
        cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
      };
      return;
    }

    // ── Horizontal swipe end (existing) ─────────────────────
    // Suppress subsequent click if any drag movement occurred
    if (t.locked || Math.abs(t.deltaX) > 0) {
      swipeJustCommitted.current = true;
    }

    const absDx = Math.abs(t.deltaX);
    const direction = t.direction;

    if (absDx > SWIPE_THRESHOLD && direction) {
      setSwipedIndex(null);
      setSwipeProgress(null);
      touchRef.current = {
        startX: 0, startY: 0, index: null, deltaX: 0,
        locked: false, direction: null,
        reorderActive: false, fromIndex: null, currentDragIndex: null,
        cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
      };
      if (onSwipeCommit) {
        const ratio = Math.min(1, absDx / DRAG_MAX);
        if (ratio >= COMMIT_RATIO) {
          onSwipeCommit(direction, index);
        } else {
          onSwipeCancel?.();
        }
      }
      return;
    }

    // Not enough movement — snap closed
    setSwipedIndex(null);
    setSwipeProgress(null);
    onSwipeCancel?.();
    touchRef.current = {
      startX: 0, startY: 0, index: null, deltaX: 0,
      locked: false, direction: null,
      reorderActive: false, fromIndex: null, currentDragIndex: null,
      cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
    };
  }, [moveMode, onSwipeCommit, onSwipeCancel, cardRefs, onReorderEnd, enableReorder, enabled]);

  // ── Touch handlers ───────────────────────────────────────────
  const handleTouchStart = useCallback((e, index) => {
    if (moveMode) return;
    const touch = e.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      index,
      deltaX: 0,
      locked: false,
      direction: null,
      reorderActive: false, fromIndex: null, currentDragIndex: null,
      cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
    };
    // If reorder is enabled, preempt the browser's pan-y claim
    // immediately so vertical drag can be intercepted by JS.
    if (enableReorder && enabled) {
      const cardEl = cardRefs?.current?.[index];
      if (cardEl) {
        cardEl.style.touchAction = 'none';
      }
    }
  }, [moveMode, enableReorder, enabled, cardRefs]);

  // React's onTouchMove is passive — cannot call preventDefault().
  // All touch-move handling is done by the native document-level
  // listener below (which attaches with { passive: false }).
  const handleTouchMove = useCallback((/* e, index */) => {
    // no-op — native listener handles everything
  }, []);

  const handleTouchEnd = useCallback((/* e, index */) => {
    // no-op — native listener handles everything
  }, []);

  // ── Mouse handlers (desktop drag) ────────────────────────────
  const handleMouseDown = useCallback((e, index) => {
    if (moveMode) return;
    // Only handle primary (left) button
    if (e.button !== 0) return;
    e.preventDefault(); // prevent text selection during drag

    mouseActive.current = true;
    touchRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      index,
      deltaX: 0,
      locked: false,
      direction: null,
      reorderActive: false, fromIndex: null, currentDragIndex: null,
      cardHeight: 0, offsetY: 0, initialTop: 0, cardWidth: 0,
    };
  }, [moveMode]);

  // Global mouse-move / mouse-up listeners — attached once
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!mouseActive.current) return;
      const t = touchRef.current;
      if (t.index === null) return;
      handleDragMove(e.clientX, e.clientY, t.index);
    };

    const onMouseUp = (e) => {
      if (!mouseActive.current) return;
      mouseActive.current = false;
      const t = touchRef.current;
      if (t.index === null) return;
      handleDragEnd(t.index);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  // Native document-level touchmove/touchend listeners (non-passive).
  // React's onTouchMove is passive and cannot call preventDefault(),
  // so we use native listeners to suppress browser scroll during reorder.
  useEffect(() => {
    const onNativeTouchMove = (e) => {
      if (moveModeRef.current) return;
      const t = touchRef.current;
      if (t.index === null) return;
      // Suppress native scroll when reorder is enabled or active
      if (t.reorderActive || (enableReorderRef.current && enabledRef.current)) {
        e.preventDefault();
      }
      // Delegate to the shared drag-move logic
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY, t.index);
    };

    const onNativeTouchEnd = (e) => {
      if (moveModeRef.current) return;
      const t = touchRef.current;
      if (t.index === null) return;
      handleDragEnd(t.index);
    };

    document.addEventListener('touchmove', onNativeTouchMove, { passive: false });
    document.addEventListener('touchend', onNativeTouchEnd);
    return () => {
      document.removeEventListener('touchmove', onNativeTouchMove);
      document.removeEventListener('touchend', onNativeTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  // ── Safety cleanup: restore container overflow on unmount ──────
  // If the component unmounts mid-drag, the scroll container could be
  // left with overflow-y: hidden.  This effect cleans up regardless of
  // how the drag ends.
  useEffect(() => {
    return () => {
      const t = touchRef.current;
      if (t.reorderActive) {
        const scrollEl = scrollContainerRef?.current;
        if (scrollEl && t._origOverflowY !== undefined) {
          scrollEl.style.overflowY = t._origOverflowY;
        }
        // Also clean up the dragged card if it still has fixed positioning
        if (t.index !== null) {
          const cardEl = cardRefs?.current?.[t.index];
          if (cardEl) {
            cardEl.style.position = '';
            cardEl.style.left = '';
            cardEl.style.top = '';
            cardEl.style.width = '';
            cardEl.style.zIndex = '';
            cardEl.style.touchAction = '';
            cardEl.classList.remove('dragging');
          }
        }
      }
    };
  }, [scrollContainerRef, cardRefs]);

  return {
    touchHandlers: { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd },
    mouseHandlers: { onMouseDown: handleMouseDown },
    swipedIndex,
    swipeProgress,
    swipeJustCommitted,
    closeSwipe,
    reorderState,
  };
}

export { SWIPE_THRESHOLD, DRAG_MAX, COMMIT_RATIO };
